/**
 * Interactive content rendered inside every blue "Available" event.
 *
 * Behaviour (legacy parity with `ManualAvailableContent` in
 * KInterviewScheduler):
 *
 *   1. Hovering the slot tracks the mouse Y position and maps it back
 *      to a time inside the availability window, snapped DOWN to the
 *      nearest 15-minute grid.
 *   2. From that moment we build a preview block of length `duration`
 *      — clipped so it never escapes the slot's end.
 *   3. A floating overlay shows `+ HH:MM` at the preview position so
 *      the user can see exactly what they're about to book.
 *   4. Clicking the slot fires `onSelect` with the preview start/end,
 *      which books an interview of the chosen duration at that moment.
 */
import { useCallback, useMemo, useRef, useState, type MouseEvent } from 'react';
import { Box, Flex } from '@chakra-ui/react';
import { PlusCircle } from '@kula-ai/icons-core';
import moment, { type Moment } from 'moment-timezone';
import { KText } from '@/ui-library/components/text';

const HOVER_SNAP_MINUTES = 15;

interface AvailableSlotContentProps {
  /** The slot's ISO start. */
  startIso: string;
  /** The slot's ISO end. */
  endIso: string;
  /** Currently selected interview duration, in minutes. */
  duration: number;
  /** Panel timezone; all math is performed in this zone. */
  timezone: string;
  /** Fired when the user clicks inside the slot. */
  onSelect: (_slot: { start: string; end: string }) => void;
  /**
   * Colour family. `'interviewer'` (default) is the blue computed-
   * availability block. `'candidate'` is the green candidate-collected
   * availability block (SCH-57) — same hover-to-select behaviour, green
   * tones, and an extra name line. Defaulting to `'interviewer'` keeps
   * the existing blue path byte-identical.
   */
  tone?: 'interviewer' | 'candidate';
  /** Optional second line under "Available" — the candidate name on
   *  candidate-tone blocks (legacy parity). */
  subLabel?: string;
  /**
   * Earliest clearance (minutes) the resolved start must sit after `now`.
   * Mirrors the calendar's `earliestStartMs` (now + before-buffer) so a
   * hover/click can't pick a slot that starts in the past — the
   * availability window itself can straddle `now` (e.g. today's collected
   * candidate availability viewed mid-afternoon), and FullCalendar only
   * greys the past visually; it doesn't block this click handler.
   */
  bufferBeforeMinutes?: number;
}

interface Preview {
  start: Moment;
  end: Moment;
  topPct: number;
  heightPct: number;
}

export const AvailableSlotContent = ({
  startIso,
  endIso,
  duration,
  timezone,
  onSelect,
  tone = 'interviewer',
  subLabel,
  bufferBeforeMinutes = 0,
}: AvailableSlotContentProps) => {
  const isCandidate = tone === 'candidate';
  const labelColor = isCandidate ? 'pastel-2-dark' : 'primary-900';
  const previewBg = isCandidate ? 'pastel-2-light' : 'primary-25';
  const previewShadow = isCandidate
    ? '0px 4px 10px rgba(34, 134, 87, 0.18)'
    : '0px 4px 10px rgba(114, 131, 224, 0.18)';
  const containerRef = useRef<HTMLDivElement>(null);
  const [preview, setPreview] = useState<Preview | null>(null);

  const eventStart = useMemo(
    () => moment.tz(startIso, timezone),
    [startIso, timezone]
  );
  const eventEnd = useMemo(
    () => moment.tz(endIso, timezone),
    [endIso, timezone]
  );

  const handleMouseMove = useCallback(
    (e: MouseEvent<HTMLDivElement>) => {
      const el = containerRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const mouseY = e.clientY - rect.top;
      if (mouseY < 0 || mouseY > rect.height) {
        setPreview(null);
        return;
      }
      const totalMinutes = eventEnd.diff(eventStart, 'minutes');
      if (totalMinutes <= 0) {
        setPreview(null);
        return;
      }
      const minutesFromStart = Math.max(
        0,
        Math.min(totalMinutes, (mouseY / rect.height) * totalMinutes)
      );
      const snappedMinutes =
        Math.floor(minutesFromStart / HOVER_SNAP_MINUTES) * HOVER_SNAP_MINUTES;

      // 1. Start by snapping the hover position DOWN to the nearest
      //    15-minute boundary inside the availability window.
      let start = eventStart
        .clone()
        .add(snappedMinutes, 'minutes')
        .seconds(0)
        .milliseconds(0);
      let end = start.clone().add(duration, 'minutes');

      // 2. If the full-duration block would run past the window's end,
      //    pull the start back so the block ends exactly at `eventEnd`
      //    (legacy `ManualAvailableContent.tsx` behaviour). Only when
      //    the slot itself is shorter than the duration do we give up
      //    and pin start to `eventStart` — the preview will then
      //    visually overflow, which is the intentional cue that the
      //    selected duration doesn't fit here.
      if (end.isAfter(eventEnd)) {
        const pulledBack = eventEnd.clone().subtract(duration, 'minutes');
        start = pulledBack.isBefore(eventStart)
          ? eventStart.clone()
          : pulledBack;
        end = start.clone().add(duration, 'minutes');
      }

      // 2b. Floor the resolved start at `now` (+ any before-buffer). The
      //     other selection paths on this calendar already reject past
      //     starts — grid drag via `selectAllow`, busy-event click — but
      //     this hover-to-select block was the lone gap, so a window that
      //     straddles `now` (e.g. today's collected candidate availability
      //     viewed mid-afternoon) let the recruiter pick a slot in the past.
      const earliestMs =
        moment().tz(timezone).valueOf() +
        (bufferBeforeMinutes > 0 ? bufferBeforeMinutes * 60_000 : 0);
      if (start.valueOf() < earliestMs) {
        setPreview(null);
        return;
      }

      // 3. Height is always the **full duration** (clipped to a minimum
      //    of the 4% visual floor so tiny previews remain hoverable).
      //    Overflow is allowed — the block reaches past the slot when
      //    the duration exceeds the slot length, matching legacy.
      const slotOffsetMin = start.diff(eventStart, 'minutes');
      const topPct = (slotOffsetMin / totalMinutes) * 100;
      const heightPct = Math.max(4, (duration / totalMinutes) * 100);
      setPreview((prev) => {
        if (prev && prev.start.isSame(start)) return prev;
        return { start, end, topPct, heightPct };
      });
    },
    [duration, eventStart, eventEnd, timezone, bufferBeforeMinutes]
  );

  const handleMouseLeave = useCallback(() => {
    setPreview(null);
  }, []);

  const handleClick = useCallback(
    (e: MouseEvent<HTMLDivElement>) => {
      if (!preview) return;
      e.stopPropagation();
      onSelect({
        start: preview.start.toISOString(),
        end: preview.end.toISOString(),
      });
    },
    [preview, onSelect]
  );

  // Column layout (icon + "Select slot" + time stacked) needs roughly
  // 60px of vertical room — icon 14px, two text lines 18px each, plus
  // padding + gap. The preview's pixel height scales linearly with
  // duration (FC's default ~1px/min), so anything below ~60 min lands
  // in compact (row) mode where icon + time fit on a single line and
  // never escape the box.
  const isCompact = duration < 60;
  const timeLabel = preview ? preview.start.format('h:mm A') : '';
  // Candidate blocks show the window's own time range as a static line
  // (legacy parity: "Available / 9:00 AM - 6:00 PM / <name>"). Blue
  // interviewer blocks keep just the "Available" label.
  const timeRangeLabel = isCandidate
    ? `${eventStart.format('h:mm A')} - ${eventEnd.format('h:mm A')}`
    : '';

  return (
    <Box
      ref={containerRef}
      position="relative"
      w="100%"
      h="100%"
      cursor="pointer"
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      onClick={handleClick}
    >
      <Box p="2px 6px" h="100%" overflow="hidden">
        <KText color={labelColor} variant="small_text" fontWeight="650">
          Available
        </KText>
        {timeRangeLabel && (
          <KText
            color={labelColor}
            variant="small_text"
            fontWeight="550"
            isTruncated
            maxW="100%"
          >
            {timeRangeLabel}
          </KText>
        )}
        {subLabel && (
          <KText
            color={labelColor}
            variant="small_text"
            fontWeight="500"
            isTruncated
            maxW="100%"
          >
            {subLabel}
          </KText>
        )}
      </Box>

      {preview && (
        <Flex
          position="absolute"
          left="0"
          right="0"
          top={`${preview.topPct}%`}
          height={`${preview.heightPct}%`}
          bg={previewBg}
          borderRadius="6px"
          zIndex={10}
          pointerEvents="none"
          flexDir={isCompact ? 'row' : 'column'}
          align={isCompact ? 'center' : 'start'}
          gap="3px"
          p={isCompact ? '4px 6px' : '6px 8px'}
          // Clip the preview to its computed box. Without this, an odd-
          // minute duration (e.g. 31 on a 30-min available window) pushes
          // the inner labels past the slot's bottom edge — and in narrow
          // columns "Select slot" wraps to two lines, so the time label
          // visibly escapes. `minW/minH={0}` lets flex children shrink
          // below their intrinsic size so the truncation below kicks in.
          overflow="hidden"
          minW={0}
          minH={0}
          boxShadow={previewShadow}
        >
          <PlusCircle
            size={14}
            color="var(--kula-colors-secondary-700)"
            className="kula-icon"
            style={{ flexShrink: 0 }}
          />
          {!isCompact && (
            <KText
              variant="small_text"
              fontWeight="650"
              isTruncated
              maxW="100%"
            >
              Select slot
            </KText>
          )}
          <KText
            variant="small_text"
            fontWeight="550"
            textTransform="uppercase"
            isTruncated
            maxW="100%"
          >
            {timeLabel}
          </KText>
        </Flex>
      )}
    </Box>
  );
};
